
import { supabase } from "@/integrations/supabase/client";
import { LearningObjective, TaxonomyLevel } from "@/types/supabase";

/**
 * Creates a new learning objective
 */
export const createLearningObjective = async (objective: Omit<LearningObjective, 'id' | 'created_at'>): Promise<LearningObjective | null> => {
  const { data, error } = await supabase
    .from('learning_objectives')
    .insert(objective)
    .select()
    .single();

  if (error) {
    console.error('Error creating learning objective:', error);
    return null;
  }

  return {
    ...data,
    taxonomy_level: data.taxonomy_level as TaxonomyLevel
  } as LearningObjective;
};

/**
 * Updates an existing learning objective
 */
export const updateLearningObjective = async (objectiveId: string, updates: Partial<LearningObjective>): Promise<LearningObjective | null> => {
  const { data, error } = await supabase
    .from('learning_objectives')
    .update(updates)
    .eq('id', objectiveId)
    .select()
    .single();

  if (error) {
    console.error('Error updating learning objective:', error);
    return null;
  }

  return data as LearningObjective;
};

/**
 * Deletes a learning objective
 */
export const deleteLearningObjective = async (objectiveId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('learning_objectives')
    .delete()
    .eq('id', objectiveId);

  if (error) {
    console.error('Error deleting learning objective:', error);
    return false;
  }

  return true;
};

/**
 * Fetches learning objectives for a module
 */
export const fetchModuleLearningObjectives = async (moduleId: string): Promise<LearningObjective[]> => {
  const { data, error } = await supabase
    .from('learning_objectives')
    .select('*')
    .eq('module_id', moduleId)
    .order('taxonomy_level', { ascending: true });

  if (error) {
    console.error('Error fetching learning objectives:', error);
    return [];
  }

  return data.map(obj => ({
    ...obj,
    taxonomy_level: obj.taxonomy_level as TaxonomyLevel
  })) as LearningObjective[];
};

/**
 * Fetches a specific learning objective by ID
 */
export const fetchLearningObjectiveById = async (objectiveId: string): Promise<LearningObjective | null> => {
  const { data, error } = await supabase
    .from('learning_objectives')
    .select('*')
    .eq('id', objectiveId)
    .single();

  if (error) {
    console.error('Error fetching learning objective:', error);
    return null;
  }

  return {
    ...data,
    taxonomy_level: data.taxonomy_level as TaxonomyLevel
  } as LearningObjective;
};
